/**
 * Jovii — the catalogue, straight from the browser.
 *
 * Cinemeta (https://v3-cinemeta.strem.io) sends `Access-Control-Allow-Origin: *`
 * and answers in ~80 ms, so there is no reason to put our server in the middle of
 * it. Artwork is images.metahub.space / episodes.metahub.space, loaded as plain
 * <img> — which is also why COOP/COEP must never be enabled: metahub sends neither
 * ACAO nor Cross-Origin-Resource-Policy.
 *
 * Owned by the CATALOGUE UI agent.
 */

import type { CatalogItem, Episode, MediaKind, Season } from '$types';
import { sharedRequests } from './shared-request';
import { catalogueDeadline, CINEMETA_DEADLINE_MS } from './catalogue-deadline';

const BASE = 'https://v3-cinemeta.strem.io';
const ART = 'https://images.metahub.space';

/* ────────────────────────────────────────────────────────────────────────────
 * Wire shapes. Cinemeta is loosely typed; everything here is optional.
 * ──────────────────────────────────────────────────────────────────────────── */

interface RawMeta {
  id?: string;
  imdb_id?: string;
  type?: string;
  name?: string;
  poster?: string;
  background?: string;
  logo?: string;
  description?: string;
  imdbRating?: string;
  genres?: string[];
  genre?: string[];
  runtime?: string;
  releaseInfo?: string;
  year?: string;
  released?: string;
  cast?: string[];
  director?: string[];
  writer?: string[];
  country?: string;
  status?: string;
  videos?: RawVideo[];
  /** `[{ source: '<ytId>', type: 'Trailer' }]`. Every one of 554 sampled entries
   *  was type Trailer and a YouTube id; there is nothing else to prefer. */
  trailers?: { source?: string; type?: string }[];
  /** The newer field, same ids under a different name. Present on 93% of rows. */
  trailerStreams?: { ytId?: string; title?: string }[];
  /** TMDB's id, riding along on every meta — saves the cast lane a /find call. */
  moviedb_id?: number;
}

interface RawVideo {
  id?: string;
  name?: string;
  title?: string;
  season?: number;
  episode?: number;
  number?: number;
  overview?: string;
  description?: string;
  thumbnail?: string;
  released?: string;
  firstAired?: string;
  rating?: string;
}

/** A film or series with everything the detail page shows. */
export interface TitleDetail extends CatalogItem {
  cast: string[];
  director: string[];
  writer: string[];
  country?: string;
  status?: string;
  /** TMDB's id, used by the cast lane only when the owner has pasted a key. */
  moviedbId?: number;
  /** Series only, grouped and sorted. Specials (season 0) come last. */
  seasons: Season[];
}

/* ────────────────────────────────────────────────────────────────────────────
 * Artwork
 * ──────────────────────────────────────────────────────────────────────────── */

/** Metahub poster at a chosen size. Deterministic from the IMDb id. */
export function posterUrl(imdbId: string, size: 'small' | 'medium' = 'medium'): string {
  return `${ART}/poster/${size}/${imdbId}/img`;
}

/**
 * Metahub backdrop. `medium` is 1920×1080 at a median 379 KB; `small` is 480×270
 * at a median 20.5 KB and appears in NO Cinemeta field — the URL has to be built,
 * the way `sharpen()` builds the poster one. Fifty backdrops at medium is 23.5 MB
 * and at small is 1.1 MB, so anything smaller than a hero asks for `small`.
 * (Measured in docs/METADATA.md § B3.)
 */
export function backgroundUrl(imdbId: string, size: 'small' | 'medium' = 'medium'): string {
  return `${ART}/background/${size}/${imdbId}/img`;
}

/** The same backdrop at 480×270, whatever size Cinemeta handed us. */
export function thumbBackground(url: string | undefined, imdbId: string): string {
  if (!url) return backgroundUrl(imdbId, 'small');
  if (url.includes('images.metahub.space')) return url.replace('/background/medium/', '/background/small/');
  return url;
}

export function logoUrl(imdbId: string): string {
  return `${ART}/logo/medium/${imdbId}/img`;
}

/**
 * A trailer's still frame from YouTube's thumbnail host — THE FALLBACK, not the
 * choice. Use `background` (via `thumbBackground`) wherever there is one.
 *
 * `hqdefault` is gone from here and must not come back: measured across 98 ids,
 * it is a 4:3 PAD OF A 16:9 FRAME IN 98 OF 98 CASES, and so is `sddefault`
 * (docs/TRAILERS.md § E2). Every tile that asked for it was cropping black bars.
 *
 * `mqdefault` is 320x180, a true 16:9, a median 11.5 KB and available for 98 of
 * 100 ids — the smallest honest picture there is. `maxresdefault` is 117.5 KB
 * and missing for 8 of 100, so it is only worth asking for where the picture is
 * large. Both send `access-control-allow-origin: *`.
 *
 * And both carry the UPLOADER'S OWN GRAPHICS: the Inception `maxresdefault` is a
 * KinoCheck frame with "HD TRAILER" set across it. Holding that for five seconds
 * is advertising KinoCheck, which is the class of thing the owner objected to —
 * hence `background`, which is a film still.
 */
export function trailerStill(id: string, size: 'md' | 'max' = 'md'): string {
  return `https://i.ytimg.com/vi/${id}/${size === 'max' ? 'maxresdefault' : 'mqdefault'}.jpg`;
}

/**
 * Cinemeta hands back `poster/small`, which is 320 px wide and visibly soft on a
 * phone. Upgrade metahub URLs in place; leave anything else (Amazon, TMDB) alone.
 */
function sharpen(url: string | undefined, imdbId: string): string | undefined {
  if (!url) return imdbId ? posterUrl(imdbId) : undefined;
  if (url.includes('images.metahub.space')) return url.replace('/small/', '/medium/');
  return url;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Mapping
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * A YouTube video id, or nothing.
 *
 * Cinemeta's `trailers[].source` is a bare id in every sample taken, but the
 * field is loosely typed and a full watch URL would be a perfectly reasonable
 * thing for it to become. Accept both, and refuse anything that is not the
 * 11-character id YouTube actually uses — an id that is wrong by one character
 * is an embed that fails silently, which is much worse than no trailer.
 */
function ytId(value: string | undefined): string | null {
  if (!value) return null;
  const raw = value.trim();
  const direct = /^[A-Za-z0-9_-]{11}$/.exec(raw);
  if (direct) return raw;
  const fromUrl = /(?:v=|youtu\.be\/|\/embed\/)([A-Za-z0-9_-]{11})/.exec(raw);
  return fromUrl ? fromUrl[1]! : null;
}

/** Every distinct trailer id on a meta object, best first, de-duplicated. */
function trailersOf(raw: RawMeta): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (value: string | undefined): void => {
    const id = ytId(value);
    if (id === null || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  };
  for (const t of raw.trailers ?? []) push(t?.source);
  for (const t of raw.trailerStreams ?? []) push(t?.ytId);
  // Four is already more than anybody watches, and each one is a string we carry
  // on every row of every rail.
  return out.slice(0, 4);
}

function kindOf(raw: RawMeta, fallback: MediaKind): MediaKind {
  return raw.type === 'series' ? 'series' : raw.type === 'movie' ? 'movie' : fallback;
}

function toItem(raw: RawMeta, fallbackKind: MediaKind): CatalogItem | null {
  const id = raw.id ?? raw.imdb_id;
  // Cinemeta's "New" catalogue occasionally carries entries with a numeric id and
  // no IMDb id. Nothing downstream can resolve those, so drop them here.
  if (!id || !/^tt\d+/.test(id) || !raw.name) return null;

  const item: CatalogItem = {
    id,
    kind: kindOf(raw, fallbackKind),
    name: raw.name
  };
  const year = raw.releaseInfo ?? raw.year;
  if (year) item.year = String(year);
  const poster = sharpen(raw.poster, id);
  if (poster) item.poster = poster;
  if (raw.background) item.background = raw.background;
  if (raw.logo) item.logo = raw.logo;
  if (raw.description) item.description = raw.description;
  if (raw.imdbRating) item.imdbRating = raw.imdbRating;
  const genres = raw.genres ?? raw.genre;
  if (genres?.length) item.genres = genres;
  if (raw.runtime) item.runtimeText = raw.runtime;
  if (raw.releaseInfo) item.releaseInfo = raw.releaseInfo;
  const trailers = trailersOf(raw);
  if (trailers.length) item.trailers = trailers;
  return item;
}

function toEpisode(raw: RawVideo, seriesId: string): Episode | null {
  const season = raw.season ?? 0;
  const number = raw.episode ?? raw.number;
  if (number === undefined || number === null) return null;

  const episode: Episode = {
    id: raw.id ?? `${seriesId}:${season}:${number}`,
    seriesId,
    season,
    episode: number,
    title: raw.name ?? raw.title ?? `Episode ${number}`
  };
  const overview = raw.overview ?? raw.description;
  if (overview) episode.overview = overview;
  if (raw.thumbnail) episode.thumbnail = raw.thumbnail;
  const released = raw.released ?? raw.firstAired;
  if (released) episode.released = released;
  return episode;
}

function toSeasons(videos: RawVideo[] | undefined, seriesId: string): Season[] {
  if (!videos?.length) return [];
  const bySeason = new Map<number, Episode[]>();
  for (const raw of videos) {
    const episode = toEpisode(raw, seriesId);
    if (!episode) continue;
    const list = bySeason.get(episode.season) ?? [];
    list.push(episode);
    bySeason.set(episode.season, list);
  }
  const seasons: Season[] = [];
  for (const [season, episodes] of bySeason) {
    episodes.sort((a, b) => a.episode - b.episode);
    seasons.push({ season, episodes });
  }
  // Specials (season 0) last; everything else ascending.
  seasons.sort((a, b) => (a.season === 0 ? 1 : b.season === 0 ? -1 : a.season - b.season));
  return seasons;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Fetching, with a small cache
 * ──────────────────────────────────────────────────────────────────────────── */

const CACHE_MS = 6 * 60 * 60 * 1000;
const cache = new Map<string, { at: number; value: unknown }>();
const request = sharedRequests<unknown>();

export class CatalogError extends Error {
  constructor(
    message: string,
    readonly status?: number
  ) {
    super(message);
    this.name = 'CatalogError';
  }
}

/**
 * True when a failed lookup is the catalogue CLEANLY saying "no such title"
 * — the only kind of answer that may count a candidate out of a pool. Both
 * shapes of a clean miss come here as a 404: an HTTP 404, and the series
 * endpoint's honest `{}` (which `detail()` turns into a 404 CatalogError).
 * Anything else — the network unreachable (no status at all), a 5xx, a
 * rate-limit, an abort — is a bad moment, not a fact about the title: AN
 * ERRORED CANDIDATE IS NOT A NONEXISTENT ONE, and the fill loops that walk
 * candidate pools (AwardRail, Person) must never conclude exhaustion from
 * one. They park it and try again instead.
 */
export function definiteMiss(err: unknown): boolean {
  return err instanceof CatalogError && err.status === 404;
}

async function getJson<T>(path: string, signal?: AbortSignal, ttl = CACHE_MS): Promise<T> {
  signal?.throwIfAborted();
  const hit = cache.get(path);
  if (hit && Date.now() - hit.at < ttl) return hit.value as T;

  return await request(path, (parent) => catalogueDeadline(parent, CINEMETA_DEADLINE_MS, 'Catalogue request', async (signal) => {
    let response: Response;
    try {
      response = await fetch(`${BASE}/${path}`, {
        signal: signal ?? null,
        credentials: 'omit',
        headers: { accept: 'application/json' }
      });
    } catch (err) {
      signal.throwIfAborted();
      if ((err as Error)?.name === 'AbortError') throw err;
      throw new CatalogError('Cannot reach the catalogue. Check your connection.');
    }
    if (!response.ok) {
      throw new CatalogError(`Catalogue returned ${response.status}`, response.status);
    }
    const value = (await response.json()) as T;
    signal.throwIfAborted();
    cache.set(path, { at: Date.now(), value });
    return value;
  }), signal) as T;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Public API
 * ──────────────────────────────────────────────────────────────────────────── */

/** Cinemeta's catalogue ids, as declared by its manifest. */
export type CatalogId = 'top' | 'year' | 'imdbRating';

/**
 * Cinemeta's genre vocabulary, exactly as its manifest declares it — these strings
 * go on the wire as `genre=…` and a near miss returns an empty shelf, so they are
 * not prettified anywhere. Films have 19; series have the same 19 plus three that
 * only exist on television. (docs/METADATA.md § D.)
 */
const GENRES_SHARED = [
  'Action',
  'Adventure',
  'Animation',
  'Biography',
  'Comedy',
  'Crime',
  'Documentary',
  'Drama',
  'Family',
  'Fantasy',
  'History',
  'Horror',
  'Mystery',
  'Romance',
  'Sci-Fi',
  'Sport',
  'Thriller',
  'War',
  'Western'
] as const;

const GENRES_SERIES_ONLY = ['Reality-TV', 'Talk-Show', 'Game-Show'] as const;

/** Every genre this kind actually has, in the order they are shown. */
export function genresFor(kind: MediaKind): string[] {
  return kind === 'series' ? [...GENRES_SHARED, ...GENRES_SERIES_ONLY] : [...GENRES_SHARED];
}

/** True when the catalogue really has this genre for this kind. */
export function isGenre(kind: MediaKind, genre: string): boolean {
  return genresFor(kind).includes(genre);
}

export interface CatalogQuery {
  kind: MediaKind;
  catalog: CatalogId;
  genre?: string;
  skip?: number;
}

export async function catalog(query: CatalogQuery, signal?: AbortSignal): Promise<CatalogItem[]> {
  const extras: string[] = [];
  if (query.genre) extras.push(`genre=${encodeURIComponent(query.genre)}`);
  if (query.skip) extras.push(`skip=${query.skip}`);
  const suffix = extras.length ? `/${extras.join('&')}` : '';
  const path = `catalog/${query.kind}/${query.catalog}${suffix}.json`;

  const data = await getJson<{ metas?: RawMeta[] }>(path, signal);
  return (data.metas ?? []).map((m) => toItem(m, query.kind)).filter((x): x is CatalogItem => x !== null);
}

/**
 * Cinemeta answers ~50 rows a page (measured across top/year/imdbRating, with
 * and without a genre — never more, occasionally 48–49 when a row is dropped
 * server-side), and `skip` walks deeper in steps of this with no overlap.
 * Exported: it is the stride an endless rail's `more()` deepens by, one page
 * per approach (LazyRail).
 */
export const CATALOG_PAGE = 50;

/**
 * A page that comes back shorter than this is the end of the catalogue, not a
 * dropped row: real short-falls measured on live pages are one or two rows
 * (Horror's first page is 48), while the end of a genre is a page of a handful
 * or none. Between those two shapes, 40 is comfortably unambiguous. Exported
 * for the same caller: a FIRST page under this line is the whole category.
 */
export const CATALOG_PAGE_SHORT = 40;

/**
 * As `catalog()`, but willing to turn more than one page for a rail that shows
 * more than a page holds.
 *
 * POLITELY: pages are requested one after another, never in a burst, and each
 * page is the same cached-for-six-hours `getJson` path a single-page caller
 * uses — so page zero here is free when a rail already fetched it, and two
 * rails wanting the same depth cost the endpoint one crawl, not two.
 *
 * HONESTLY: it stops at the first short page, because that is the catalogue
 * saying "that is everything" — a rail whose genre only has 30 titles gets 30,
 * never padding, never a fourth request to make sure. De-duplicated by id
 * across pages as a courtesy to `{#each}` keys; measured today the pages do
 * not overlap, but this endpoint is loosely specified and a repeated id would
 * crash every rail drawn from it.
 */
export async function catalogDeep(query: CatalogQuery, want: number, signal?: AbortSignal): Promise<CatalogItem[]> {
  const out: CatalogItem[] = [];
  const seen = new Set<string>();

  for (let skip = 0; out.length < want; skip += CATALOG_PAGE) {
    const page = await catalog(skip > 0 ? { ...query, skip } : query, signal);
    for (const item of page) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      out.push(item);
    }
    if (page.length < CATALOG_PAGE_SHORT) break;
  }

  return out.slice(0, want);
}

/**
 * Search both catalogues and interleave, because "breaking bad" and "the matrix"
 * should both be one tap away without the user first choosing a media type.
 */
export async function search(query: string, signal?: AbortSignal): Promise<CatalogItem[]> {
  const q = query.trim();
  if (q.length < 2) return [];
  const encoded = encodeURIComponent(q);

  const sides = await Promise.all([
    getJson<Side>(`catalog/movie/top/search=${encoded}.json`, signal, 10 * 60 * 1000).catch(swallow),
    getJson<Side>(`catalog/series/top/search=${encoded}.json`, signal, 10 * 60 * 1000).catch(swallow)
  ]);
  const movies: Side = sides[0];
  const series: Side = sides[1];

  /**
   * ONE catalogue failing is survivable — show the other one's results. BOTH
   * failing is an outage, and swallowing it told the user their search term was
   * wrong ("Nothing called 'matrix' — try fewer words") while six requests were
   * failing with ERR_FAILED underneath. Home and Title already say "The catalogue
   * is not answering"; search was the one screen that blamed the user.
   */
  if (movies.failed === true && series.failed === true) {
    throw movies.error ?? series.error ?? new Error('cannot reach the catalogue');
  }

  const m = (movies.metas ?? []).map((x) => toItem(x, 'movie')).filter((x): x is CatalogItem => x !== null);
  const s = (series.metas ?? []).map((x) => toItem(x, 'series')).filter((x): x is CatalogItem => x !== null);

  // Cinemeta ranks within a type, not across them. Interleave 2 films : 1 series,
  // which puts the obvious answer first for both "matrix" and "breaking bad".
  const out: CatalogItem[] = [];
  let i = 0;
  let j = 0;
  while (i < m.length || j < s.length) {
    if (i < m.length) out.push(m[i++]!);
    if (i < m.length) out.push(m[i++]!);
    if (j < s.length) out.push(s[j++]!);
  }
  return out;
}

/** One catalogue's answer, or the reason there isn't one. */
interface Side {
  metas?: RawMeta[];
  failed?: boolean;
  error?: unknown;
}

function swallow(err: unknown): Side {
  if ((err as Error)?.name === 'AbortError') throw err;
  return { failed: true, error: err };
}

/** Full metadata for one title, with seasons already grouped. */
export async function detail(kind: MediaKind, imdbId: string, signal?: AbortSignal): Promise<TitleDetail> {
  const data = await getJson<{ meta?: RawMeta }>(`meta/${kind}/${imdbId}.json`, signal);
  const raw = data.meta;
  if (!raw) throw new CatalogError('That title is not in the catalogue.', 404);

  const base = toItem(raw, kind);
  if (!base) throw new CatalogError('That title is not in the catalogue.', 404);

  const detailed: TitleDetail = {
    ...base,
    cast: raw.cast ?? [],
    director: raw.director ?? [],
    writer: raw.writer ?? [],
    seasons: kind === 'series' ? toSeasons(raw.videos, imdbId) : []
  };
  if (raw.country) detailed.country = raw.country;
  if (raw.status) detailed.status = raw.status;
  if (typeof raw.moviedb_id === 'number') detailed.moviedbId = raw.moviedb_id;
  if (!detailed.background) detailed.background = backgroundUrl(imdbId);
  return detailed;
}

/** Find one episode inside an already-fetched series. */
export function findEpisode(seasons: Season[], season: number, episode: number): Episode | undefined {
  return seasons.find((s) => s.season === season)?.episodes.find((e) => e.episode === episode);
}

/** The episode after this one, crossing into the next season. Null at the end. */
export function nextEpisode(seasons: Season[], season: number, episode: number): Episode | null {
  const index = seasons.findIndex((s) => s.season === season);
  if (index < 0) return null;
  const current = seasons[index]!;
  const position = current.episodes.findIndex((e) => e.episode === episode);
  if (position >= 0 && position + 1 < current.episodes.length) return current.episodes[position + 1]!;
  const next = seasons[index + 1];
  return next?.episodes[0] ?? null;
}
